/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { getGiftCardBalanceCheck, GiftCardBalanceCheckInput } from '../../data-actions/gift-card/get-card-balance';
import { ICittaGiftCardBalanceCheckData, ICittaGiftCardBalanceCheckResult } from './citta-gift-card-balance-check.data';
import { ICittaGiftCardBalanceCheckProps } from './citta-gift-card-balance-check.props.autogenerated';
type CittaGiftCardBalanceCheckStateKeys = 'c' | 'p';

interface ICittaGiftCardBalanceCheckState {
    /**
     * The gift card number
     */
    card: string;
    /**
     * The gift card pin
     */
    pin: string;

    /**
     * The balance check result
     */
    result?: ICittaGiftCardBalanceCheckResult;

    /**
     * Flag to determine if we are processing a lookup request
     */
    processing: boolean;
}

export interface ICittaGiftCardBalanceCheckViewProps extends ICittaGiftCardBalanceCheckProps<ICittaGiftCardBalanceCheckData>, ICittaGiftCardBalanceCheckState {
    GiftCardBalanceCheckModuleProps: IModuleProps;
    GiftCardBalanceCheckCardNodeProps: INodeProps;
    GiftCardBalanceCheckCardNode: React.ReactNode;
    GiftCardBalanceCheckPinNodeProps: INodeProps;
    GiftCardBalanceCheckPinNode: React.ReactNode;
    GiftCardBalanceCheckResultNode: React.ReactNode;
    GiftCardBalanceCheckButtonNode: React.ReactNode;
}

/**
 *
 * CittaGiftCardBalanceCheck component
 * @extends {React.PureComponent<ICittaGiftCardBalanceCheckProps<ICittaGiftCardBalanceCheckData>, ICittaGiftCardBalanceCheckState>}
 */
class CittaGiftCardBalanceCheck extends React.PureComponent<ICittaGiftCardBalanceCheckProps<ICittaGiftCardBalanceCheckData>, ICittaGiftCardBalanceCheckState> {
    private static defaultConfig: string[] = ['Card Number', 'Pin Number', 'Checking...'];
    private static propsIndex: string[] = ['cardNumberPlaceholderText', 'pinNumberPlaceholderText', 'lookupWaitText'];
    /**
     * Gift Card Ref
     */
    private cardRef: React.RefObject<HTMLInputElement>;
    /**
     * Pin for gift card Ref
     */
    private pinRef: React.RefObject<HTMLInputElement>;

    public constructor(props: ICittaGiftCardBalanceCheckProps<ICittaGiftCardBalanceCheckData>) {
        super(props);

        // binds
        this._checkBalance = this._checkBalance.bind(this);
        this._areRefsValid = this._areRefsValid.bind(this);
        this._renderLookupResult = this._renderLookupResult.bind(this);
        this._renderFailure = this._renderFailure.bind(this);

        // refs
        this.cardRef = React.createRef();
        this.pinRef = React.createRef();

        // state init
        this.state = { card: '', pin: '', processing: false };
    }
    public render(): JSX.Element | null {
        const config = this.props.config;
        this._mergeWithDefaults();

        const CittaGiftCardBalanceCheckViewProps = {
            ...this.props,
            GiftCardBalanceCheckModuleProps: {
                moduleProps: this.props,
                className: classnames('citta-gift-card-balance-check', config.className)
            },
            GiftCardBalanceCheckCardNodeProps: {
                className: 'card-number-wrapper input-wrapper'
            },
            GiftCardBalanceCheckCardNode: this._renderCard(),
            GiftCardBalanceCheckPinNodeProps: {
                className: 'card-pin-wrapper input-wrapper'
            },
            GiftCardBalanceCheckPinNode: this._renderPin(),
            GiftCardBalanceCheckResultNode: this._renderLookupResult(),
            GiftCardBalanceCheckButtonNode: this._renderCheckBalance()
        };

        return this.props.renderView(CittaGiftCardBalanceCheckViewProps) as React.ReactElement;
    }

    private _mergeWithDefaults(): void {
        const propsIndex = CittaGiftCardBalanceCheck.propsIndex;
        const defaultConfig = CittaGiftCardBalanceCheck.defaultConfig;
        const config = this.props.config;
        for (let keyIndex = 0; keyIndex < propsIndex.length; keyIndex++) {
            const keyName = propsIndex[keyIndex];
            config[keyName] = config[keyName] || defaultConfig[keyIndex];
        }
    }

    private _renderCard(): React.ReactNode {
        const { cardNumberPlaceholderText } = this.props.config;
        return (
            <input
                type='text'
                ref={this.cardRef}
                placeholder={cardNumberPlaceholderText}
                aria-label='Card Number'
                className='cardnum gc-input'
                name='cardnum'
                onChange={this._capture('c')}
            />
        );
    }

    private _renderPin(): React.ReactNode {
        const { pinNumberPlaceholderText } = this.props.config;
        return (
            <input
                type='text'
                ref={this.pinRef}
                placeholder={pinNumberPlaceholderText}
                aria-label='Pin Number'
                className='pin gc-input'
                name='cardpin'
                onChange={this._capture('p')}
            />
        );
    }

    private _renderCheckBalance(): React.ReactNode {
        const submitButtonProps: React.ButtonHTMLAttributes<{}> = {
            // disable if we're invalid or if we're processing a request
            disabled: !this._areRefsValid() || this.state.processing
        } as React.ButtonHTMLAttributes<{}>;
        return (
            <button
                className='btn check-balance'
                onClick={this._checkBalance}
                {...submitButtonProps}
                type='button'
            >
                {this.props.resources.buttonTextForGiftCardBalanceCheck || 'check balance'}
            </button>
        );
    }

    private _renderLookupResult = (): React.ReactNode | null  => {
        const { result } = this.state;
        if (!result) {
            return;
        }
        if (this.state.processing) {
            return <div className='gift-card-loading-wrapper'>{this.props.config.lookupWaitText}</div>;
        }
        const { success, balance, message } = result;
        const className = success ? 'success' : 'fail';
        return (
            <div className={`result-wrapper ${className}`}>
                {
                    success ? this._renderSuccess(message, balance) : this._renderFailure(message)
                }
            </div>
        );
    }

    private _renderFailure(message?: string): React.ReactNode | null {
        return <div className='message-wrapper'>{message || this.props.config.lookupFailureMessage}</div>;
    }

    private _renderSuccess(message?: string, balance?: number): React.ReactNode | null {
        const formattedBalance: string = this.props.context.cultureFormatter.formatCurrency(balance || '' as React.ReactText);
        return (
            <>
                {message && <div className='message-wrapper'>{message}</div>}
                <div className='balance-wrapper'>{this.props.resources.labelForSuccessfulGiftCardBalanceCheckResponse || 'Balance: '} <span className='balance'>{formattedBalance}</span></div>
            </>
        );
    }

    private _areRefsValid(): boolean {
        const refKeys = ['cardRef', 'pinRef'];
        for (let keyIndex = 0; keyIndex < refKeys.length; keyIndex++) {
            const ref: React.RefObject<HTMLInputElement> | undefined = this[refKeys[keyIndex]];
            if (!ref || !ref.current || !ref.current.value) {
                return false;
            }
        }
        return true;
    }

    private _capture = (key: CittaGiftCardBalanceCheckStateKeys) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const newState = { [key]: event.target.value };
        // @ts-ignore -- type is valid
        this.setState(newState);
    }

    private _checkBalance(): void {
        this.setState(previousState => {
            return {
                ...previousState,
                processing: true
            };
            // tslint:disable-next-line:align
        }, () => {
            // tslint:disable-next-line:no-floating-promises
            getGiftCardBalanceCheck(new GiftCardBalanceCheckInput(this.cardRef.current!.value, this.pinRef.current!.value), this.props.context.actionContext)
                .then((result) => {
                    this.cardRef && this.cardRef.current && this.cardRef.current.value ? this.cardRef.current.value = '' : null;
                    this.pinRef && this.pinRef.current && this.pinRef.current.value ? this.pinRef.current.value = '' : null;
                    this.setState({
                        result,
                        processing: false
                    });
                });
        });
    }
}

export default CittaGiftCardBalanceCheck;
